import React, { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { reorderByDate } from '../../utils/jobOrdering'
import { validateSegments, segmentRange, writeSegments } from '../../lib/segments'
import SegmentsEditor from './SegmentsEditor'

const POOLS_JOB_TYPES = [
  { value: 'above_ground', label: 'Above Ground Pool' },
  { value: 'inground_residential', label: 'Inground - Residential' },
  { value: 'inground_commercial', label: 'Inground - Commercial' },
  { value: 'renovation', label: 'Renovation' },
  { value: 'repair', label: 'Repair' },
  { value: 'landscaping', label: 'Landscaping' },
]

const RIVERBEND_JOB_TYPES = [
  { value: 'land_clearing', label: 'Land Clearing' },
  { value: 'mulching', label: 'Mulching' },
  { value: 'stump_grinding', label: 'Stump Grinding' },
  { value: 'site_prep', label: 'Site Preparation' },
  { value: 'brush_removal', label: 'Brush Removal' },
]

export default function NewJobModal({ defaultDate, activeBusiness, onClose, onSave }) {
  
  const [form, setForm] = useState({
    business_id: activeBusiness !== 'all' ? activeBusiness : 'pools',
    title: '', job_type: '', client_name: '', client_phone: '', client_email: '',
    site_address: '', site_city: '', site_state: 'MI', site_zip: '',
    contract_value: '', start_date: defaultDate || '', end_date: defaultDate || '',
    crew_leader_id: '', notes: '', pool_type: '', pool_dimensions: '', dig_specs: '',
    utility_locate_status: 'pending', acreage: '', debris_disposal: '', site_access_notes: '',
    crew_size_needed: activeBusiness === 'riverbend' ? 3 : 5,
  })
  const [crewLeaders, setCrewLeaders] = useState([])
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [multiVisit, setMultiVisit] = useState(false)
  const [segments, setSegments] = useState([{ segment_number: 1, start_date: defaultDate || '', end_date: defaultDate || '', description: '', crew_size_needed: '' }])
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))
  const isRiverbend = form.business_id === 'riverbend'
  const jobTypes = form.business_id === 'pools' ? POOLS_JOB_TYPES : RIVERBEND_JOB_TYPES

  useEffect(() => {
    const fetchLeaders = async () => {
      const { data } = await supabase.from('profiles')
        .select('id, full_name, role, availability_status')
        .in('role', ['project_manager', 'owner', 'crew_leader'])
        .contains('business_ids', [form.business_id])
      // Prefer in-season workers; show others but marked
      setCrewLeaders(data || [])
    }
    fetchLeaders()
  }, [form.business_id])

  const useSegments = isRiverbend && multiVisit
  const segValidation = useSegments ? validateSegments(segments) : { startError: null, overlapErrors: [] }

  const handleSave = async () => {
    if (!form.client_name) { setError('Client name is required.'); return }
    let jobStart = form.start_date
    let jobEnd = form.end_date || form.start_date
    if (useSegments) {
      if (segValidation.startError) { setError(segValidation.startError); return }
      if (segValidation.overlapErrors.length > 0) { setError('Segments overlap or are out of order.'); return }
      const range = segmentRange(segments)
      jobStart = range.start_date
      jobEnd = range.end_date
    }
    if (!jobStart) { setError('Start date is required.'); return }
    setSaving(true)
    setError('')
    var jobNumber = null
    try { var { data: nn } = await supabase.rpc('get_next_job_number', { biz_id: form.business_id }); if (nn) jobNumber = nn } catch (e) {} // eslint-disable-line no-unused-vars
    const { data: inserted, error: err } = await supabase.from('jobs').insert({
      job_number: jobNumber,
      business_id: form.business_id, title: form.title || form.client_name,
      job_type: form.job_type, client_name: form.client_name, client_phone: form.client_phone,
      client_email: form.client_email, site_address: form.site_address, site_city: form.site_city,
      site_state: form.site_state, site_zip: form.site_zip,
      contract_value: form.contract_value ? parseFloat(form.contract_value) : null,
      start_date: jobStart, end_date: jobEnd,
      crew_leader_id: form.crew_leader_id || null, notes: form.notes, status: 'scheduled',
      pool_type: form.pool_type, pool_dimensions: form.pool_dimensions, dig_specs: form.dig_specs,
      utility_locate_status: form.utility_locate_status,
      acreage: form.acreage ? parseFloat(form.acreage) : null,
      debris_disposal: form.debris_disposal, site_access_notes: form.site_access_notes,
      crew_size_needed: form.crew_size_needed ? parseInt(form.crew_size_needed, 10) : (form.business_id === 'pools' ? 5 : 3),
      has_segments: useSegments,
    }).select('id').single()
    if (err) { setError(err.message); setSaving(false); return }
    if (inserted?.id && useSegments) {
      const seg = await writeSegments(inserted.id, segments)
      if (seg.error) { setError('Saved job but segments failed: ' + seg.error.message); setSaving(false); return }
    }
    if (inserted?.id && jobStart) {
      try { await reorderByDate(inserted.id, jobStart, form.business_id) } catch (e) { console.error(e) }
    }
    setSaving(false)
    onSave()
  }

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <div style={styles.modalTitle}>NEW JOB</div>
          <button onClick={onClose} style={styles.closeBtn}>✕</button>
        </div>
        <div style={styles.modalBody}>
          <div style={styles.sectionTitle}>BUSINESS & TYPE</div>
          <div style={styles.grid2}>
            <div style={styles.field}>
              <label style={styles.label}>BUSINESS</label>
              <select value={form.business_id} onChange={e => { set('business_id', e.target.value); set('job_type', '') }} style={styles.input}>
                <option value="pools">CP Quality Pools</option>
                <option value="riverbend">Riverbend Clearing</option>
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>JOB TYPE</label>
              <select value={form.job_type} onChange={e => set('job_type', e.target.value)} style={styles.input}>
                <option value="">Select type...</option>
                {jobTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          </div>
          <div style={styles.sectionTitle}>CLIENT INFO</div>
          <div style={styles.grid2}>
            <div style={styles.field}><label style={styles.label}>CLIENT NAME *</label><input value={form.client_name} onChange={e => set('client_name', e.target.value)} style={styles.input} placeholder="Full name or company" /></div>
            <div style={styles.field}><label style={styles.label}>PHONE</label><input value={form.client_phone} onChange={e => set('client_phone', e.target.value)} style={styles.input} placeholder="(810) 000-0000" /></div>
            <div style={styles.field}><label style={styles.label}>EMAIL</label><input value={form.client_email} onChange={e => set('client_email', e.target.value)} style={styles.input} placeholder="client@email.com" type="email" /></div>
            <div style={styles.field}><label style={styles.label}>CONTRACT VALUE</label><input value={form.contract_value} onChange={e => set('contract_value', e.target.value)} style={styles.input} placeholder="0.00" type="number" /></div>
          </div>
          <div style={styles.sectionTitle}>SITE ADDRESS</div>
          <div style={styles.grid2}>
            <div style={{ ...styles.field, gridColumn: '1 / -1' }}><label style={styles.label}>STREET ADDRESS</label><input value={form.site_address} onChange={e => set('site_address', e.target.value)} style={styles.input} placeholder="123 Main St" /></div>
            <div style={styles.field}><label style={styles.label}>CITY</label><input value={form.site_city} onChange={e => set('site_city', e.target.value)} style={styles.input} placeholder="City" /></div>
            <div style={styles.field}><label style={styles.label}>STATE</label><input value={form.site_state} onChange={e => set('site_state', e.target.value)} style={styles.input} placeholder="MI" /></div>
          </div>
          <div style={styles.sectionTitle}>SCHEDULE</div>
          {isRiverbend && (
            <div
              onClick={() => setMultiVisit(v => !v)}
              style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '10px 12px', backgroundColor: multiVisit ? 'rgba(74,140,106,0.08)' : 'rgba(255,255,255,0.03)', border: '1px solid ' + (multiVisit ? 'rgba(74,140,106,0.3)' : 'rgba(255,255,255,0.08)'), borderRadius: '3px', cursor: 'pointer', marginBottom: '12px' }}
            >
              <div style={{ width: '34px', height: '18px', borderRadius: '10px', backgroundColor: multiVisit ? '#4a8c6a' : 'rgba(255,255,255,0.15)', position: 'relative', transition: 'background-color 0.15s' }}>
                <div style={{ position: 'absolute', top: '2px', left: multiVisit ? '18px' : '2px', width: '14px', height: '14px', borderRadius: '50%', backgroundColor: '#fff', transition: 'left 0.15s' }} />
              </div>
              <div>
                <div style={{ fontFamily: "'Barlow Condensed',sans-serif", fontSize: '12px', letterSpacing: '1.5px', fontWeight: 600, color: multiVisit ? '#4a8c6a' : 'rgba(255,255,255,0.7)' }}>MULTI-VISIT JOB</div>
                <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>Job spans multiple visits (e.g., mulching, then return for stump grinding).</div>
              </div>
            </div>
          )}
          {!useSegments && (
            <div style={styles.grid2}>
              <div style={styles.field}><label style={styles.label}>START DATE *</label><input value={form.start_date} onChange={e => set('start_date', e.target.value)} style={styles.input} type="date" /></div>
              <div style={styles.field}><label style={styles.label}>END DATE</label><input value={form.end_date} onChange={e => set('end_date', e.target.value)} style={styles.input} type="date" /></div>
            </div>
          )}
          {useSegments && (
            <SegmentsEditor segments={segments} onChange={setSegments} validation={segValidation} defaultCrewSize={form.crew_size_needed} />
          )}
          <div style={styles.grid2}>
            <div style={styles.field}>
              <label style={styles.label}>CREW LEADER</label>
              <select value={form.crew_leader_id} onChange={e => set('crew_leader_id', e.target.value)} style={styles.input}>
                <option value="">Assign later...</option>
                {crewLeaders.map(l => (
                  <option key={l.id} value={l.id}>
                    {l.full_name}{l.availability_status && l.availability_status !== 'available' ? ' (' + l.availability_status.replace('_', ' ') + ')' : ''}
                  </option>
                ))}
              </select>
            </div>
            <div style={styles.field}>
              <label style={styles.label}>CREW SIZE NEEDED</label>
              <input
                type="number"
                min="1"
                max="15"
                value={form.crew_size_needed}
                onChange={e => set('crew_size_needed', e.target.value)}
                style={styles.input}
                placeholder={form.business_id === 'pools' ? '5' : '3'}
              />
            </div>
          </div>
          {form.business_id === 'pools' && (
            <>
              <div style={styles.sectionTitle}>POOL DETAILS</div>
              <div style={styles.grid2}>
                <div style={styles.field}><label style={styles.label}>POOL DIMENSIONS</label><input value={form.pool_dimensions} onChange={e => set('pool_dimensions', e.target.value)} style={styles.input} placeholder="16x32, 12x24, etc." /></div>
                <div style={styles.field}><label style={styles.label}>DIG SPECS</label><input value={form.dig_specs} onChange={e => set('dig_specs', e.target.value)} style={styles.input} placeholder="Depth, soil type, etc." /></div>
                <div style={styles.field}>
                  <label style={styles.label}>UTILITY LOCATE</label>
                  <select value={form.utility_locate_status} onChange={e => set('utility_locate_status', e.target.value)} style={styles.input}>
                    <option value="pending">Pending</option>
                    <option value="requested">Requested</option>
                    <option value="complete">Complete</option>
                    <option value="clear">Clear - Good to Dig</option>
                  </select>
                </div>
              </div>
            </>
          )}
          {form.business_id === 'riverbend' && (
            <>
              <div style={styles.sectionTitle}>CLEARING DETAILS</div>
              <div style={styles.grid2}>
                <div style={styles.field}><label style={styles.label}>ACREAGE</label><input value={form.acreage} onChange={e => set('acreage', e.target.value)} style={styles.input} placeholder="0.0" type="number" step="0.1" /></div>
                <div style={styles.field}><label style={styles.label}>DEBRIS DISPOSAL</label><input value={form.debris_disposal} onChange={e => set('debris_disposal', e.target.value)} style={styles.input} placeholder="On site, haul away, chip, etc." /></div>
                <div style={{ ...styles.field, gridColumn: '1 / -1' }}><label style={styles.label}>SITE ACCESS NOTES</label><input value={form.site_access_notes} onChange={e => set('site_access_notes', e.target.value)} style={styles.input} placeholder="Gate codes, road conditions, etc." /></div>
              </div>
            </>
          )}
          <div style={styles.sectionTitle}>NOTES</div>
          <textarea value={form.notes} onChange={e => set('notes', e.target.value)} style={styles.textarea} placeholder="Any additional notes..." rows={3} />
          {error && <div style={styles.error}>{error}</div>}
        </div>
        <div style={styles.modalFooter}>
          <button onClick={onClose} style={styles.cancelBtn}>CANCEL</button>
          <button onClick={handleSave} disabled={saving} style={styles.saveBtn}>{saving ? 'SAVING...' : 'CREATE JOB'}</button>
        </div>
      </div>
    </div>
  )
}

const styles = {
  overlay:{position:'fixed',inset:0,backgroundColor:'rgba(0,0,0,0.75)',zIndex:400,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'},
  modal:{backgroundColor:'#0d1420',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'6px',width:'100%',maxWidth:'720px',maxHeight:'90vh',overflow:'hidden',display:'flex',flexDirection:'column',fontFamily:"'Barlow',sans-serif"},
  modalHeader:{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'20px 24px',borderBottom:'1px solid rgba(255,255,255,0.06)'},
  modalTitle:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'20px',fontWeight:700,letterSpacing:'3px',color:'#b48c3c'},
  closeBtn:{backgroundColor:'transparent',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',color:'rgba(255,255,255,0.4)',fontSize:'14px',cursor:'pointer',padding:'6px 10px'},
  modalBody:{flex:1,overflowY:'auto',padding:'24px'},
  sectionTitle:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'11px',letterSpacing:'3px',color:'rgba(255,255,255,0.3)',marginBottom:'12px',marginTop:'20px',paddingBottom:'8px',borderBottom:'1px solid rgba(255,255,255,0.05)'},
  grid2:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'14px',marginBottom:'4px'},
  field:{display:'flex',flexDirection:'column',gap:'6px'},
  label:{fontFamily:"'Barlow Condensed',sans-serif",fontSize:'10px',letterSpacing:'2px',color:'rgba(255,255,255,0.35)'},
  input:{backgroundColor:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',padding:'10px 12px',color:'#ffffff',fontSize:'15px',fontFamily:"'Barlow',sans-serif",outline:'none',width:'100%',boxSizing:'border-box'},
  textarea:{backgroundColor:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',padding:'10px 12px',color:'#ffffff',fontSize:'15px',fontFamily:"'Barlow',sans-serif",outline:'none',width:'100%',resize:'vertical',boxSizing:'border-box'},
  error:{backgroundColor:'rgba(220,60,60,0.1)',border:'1px solid rgba(220,60,60,0.3)',borderRadius:'3px',padding:'12px 16px',color:'#ff6b6b',fontSize:'14px',marginTop:'16px'},
  modalFooter:{display:'flex',gap:'12px',justifyContent:'flex-end',padding:'16px 24px',borderTop:'1px solid rgba(255,255,255,0.06)'},
  cancelBtn:{backgroundColor:'transparent',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'3px',padding:'12px 20px',color:'rgba(255,255,255,0.5)',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'13px',letterSpacing:'2px',cursor:'pointer'},
  saveBtn:{backgroundColor:'#b48c3c',border:'none',borderRadius:'3px',padding:'12px 24px',color:'#0a0f1a',fontFamily:"'Barlow Condensed',sans-serif",fontSize:'13px',fontWeight:700,letterSpacing:'2px',cursor:'pointer'},
}
